// Thin fetch wrapper. All API routes live under /api and SWA forwards the
// authenticated user identity via x-ms-client-principal automatically.

export class ApiError extends Error {
  status: number;
  body: unknown;
  constructor(message: string, status: number, body: unknown) {
    super(message);
    this.status = status;
    this.body = body;
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(path, {
    ...init,
    headers: {
      'content-type': 'application/json',
      ...(init.headers || {}),
    },
    credentials: 'include',
  });
  if (res.status === 204) return undefined as T;

  // Parse defensively. An infrastructure failure (function host down, cold-start
  // timeout, deploy in flight) makes SWA serve an HTML error page for /api/*, and
  // blindly calling JSON.parse turned that into "Unexpected token '<'" — which hid
  // the status code and sent people to DevTools. Always surface status first.
  const text = await res.text();
  let body: unknown;
  let parsedOk = true;
  if (text) {
    try { body = JSON.parse(text); } catch { parsedOk = false; }
  }

  if (!res.ok) {
    const fromBody = parsedOk
      ? ((body as any)?.error || (body as any)?.message)
      : undefined;
    throw new ApiError(
      fromBody || `HTTP ${res.status}${res.statusText ? ` ${res.statusText}` : ''}`,
      res.status,
      parsedOk ? body : { nonJson: text.slice(0, 200) },
    );
  }

  if (!parsedOk) {
    throw new ApiError(
      `Expected JSON from ${path} but got ${res.headers.get('content-type') || 'an unknown format'}`,
      res.status,
      { nonJson: text.slice(0, 200) },
    );
  }
  return body as T;
}

export const api = {
  get:    <T>(path: string)               => request<T>(path),
  post:   <T>(path: string, body: unknown) => request<T>(path, { method: 'POST',   body: JSON.stringify(body) }),
  put:    <T>(path: string, body: unknown) => request<T>(path, { method: 'PUT',    body: JSON.stringify(body) }),
  delete: <T>(path: string)               => request<T>(path, { method: 'DELETE' }),
};
